import { en } from "./catalogs/en";
import { ko } from "./catalogs/ko";
import { validateCatalog } from "./catalog";
import type { LocaleCatalog } from "./catalog";

export interface LocaleDefinition {
  tag: string;
  catalog: LocaleCatalog;
}
export interface SystemLanguage {
  language?: string;
  languages?: readonly string[];
}

export function canonicalTag(tag: string): string | null {
  try {
    return Intl.getCanonicalLocales(tag.trim())[0] ?? null;
  } catch {
    return null;
  }
}

/** A registry is extensible data; supported languages are never a fixed union. */
export function createLocaleRegistry(definitions: readonly LocaleDefinition[]) {
  const registry = new Map<string, LocaleDefinition>();
  for (const definition of definitions) {
    const tag = canonicalTag(definition.tag);
    if (!tag) throw new Error(`Invalid locale: ${definition.tag}`);
    if (registry.has(tag)) throw new Error(`Duplicate locale: ${tag}`);
    validateCatalog(definition.catalog);
    registry.set(tag, { tag, catalog: definition.catalog });
  }
  if (!registry.has("en")) throw new Error("English fallback is required");
  return registry;
}

export const localeRegistry = createLocaleRegistry([
  { tag: "en", catalog: en },
  { tag: "ko", catalog: ko },
]);

/** Only the primary preference is consulted, including when it is unsupported. */
export function resolveLocale(
  system: SystemLanguage,
  registry = localeRegistry,
): string {
  const primary =
    system.language?.trim() || system.languages?.[0]?.trim() || "en";
  const tag = canonicalTag(primary);
  if (!tag) return "en";
  if (registry.has(tag)) return tag;
  const base = tag.split("-")[0];
  return registry.has(base) ? base : "en";
}
